import { useHubble } from '@seek/hubble';
import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonIcon,
  Column,
  Columns,
  IconThumb,
  Inline,
  Text,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import AnimatedHeight from 'src/components/AnimatedHeight/AnimatedHeight';
import { useCandidateDetails } from 'src/modules/hooks/useCandidateDetailsQuery';
import { useSelector } from 'src/store/react';
import { selectSolMetadataRequestToken } from 'src/store/selectors';
import { useMelwaysLocale } from 'src/utils/melwaysHelper';

import translations from './.vocab';

import * as styles from './SRPFeedbackPrompt.css';

const getSurveyEventName = (eventLocale: Locale) => {
  switch (eventLocale) {
    case 'en-AU':
      return 'srp_feedback_survey_AU';
    case 'en-NZ':
      return 'srp_feedback_survey_NZ';
    case 'id-ID':
      return 'srp_feedback_survey_ID';
    case 'en-ID':
      return 'srp_feedback_survey_ID_EN';
    case 'th-TH':
      return 'srp_feedback_survey_TH';
    case 'en-TH':
      return 'srp_feedback_survey_TH_EN';
    case 'zh-HK':
      return 'srp_feedback_survey_HK';
    case 'en-HK':
      return 'srp_feedback_survey_HK_EN';
    default:
      return 'srp_feedback_survey';
  }
};

export const SRPFeedbackPrompt = () => {
  const { t } = useTranslations(translations);

  const locale = useMelwaysLocale();
  const { id: seekerId } = useCandidateDetails();
  const visitorId = useHubble().visitorId();
  const requestToken = useSelector(selectSolMetadataRequestToken);

  const [srpFeedbackPromptClicked, setSRPFeedbackPromptClicked] =
    useState(false);

  useEffect(() => {
    window.hj =
      window.hj ||
      function () {
        // eslint-disable-next-line prefer-rest-params
        (hj.q = hj.q || []).push(arguments);
      };
  }, []);

  const srpFeedbackPromptHandler = () => {
    const userProps = {
      visitor_id: visitorId,
      search_event_id: requestToken,
      ...(seekerId ? { candidate_id: seekerId } : {}),
    };

    // Other places are calling the Identify API with seekerId so using it here to keep it consistent, otherwise send null if it doesn't exist
    const userId = seekerId?.toString() || null;
    window.hj('identify', userId, userProps);
    window.hj('event', getSurveyEventName(locale));

    setSRPFeedbackPromptClicked(true);
  };

  return (
    <AnimatedHeight changeKeys={[srpFeedbackPromptClicked]}>
      {!srpFeedbackPromptClicked && (
        <Box
          className={styles.srpFeedbackPrompt}
          background="neutralLight"
          borderRadius="large"
          marginTop="xsmall"
          padding="medium"
        >
          <Columns space="small" alignY="center">
            <Column>
              <Text size="standard" weight="medium">
                {t('How are your results?')}
              </Text>
            </Column>
            <Column width="content">
              <Box paddingRight="small">
                <Inline space="large">
                  <ButtonIcon
                    size="large"
                    icon={<IconThumb direction="up" />}
                    label="Good" // TODO Update with proper text
                    variant="transparent"
                    onClick={srpFeedbackPromptHandler}
                  />
                  <ButtonIcon
                    size="large"
                    icon={<IconThumb direction="down" />}
                    label="Needs Improvement" // TODO Update with proper text
                    variant="transparent"
                    onClick={srpFeedbackPromptHandler}
                  />
                </Inline>
              </Box>
            </Column>
          </Columns>
        </Box>
      )}
    </AnimatedHeight>
  );
};
